import {AfterViewInit, Component, Input, OnInit} from '@angular/core';
import {DemographicHypeAuditor, HypeAuditorGenderType} from '../../../../models/demographic/demographic-hype-auditor';
import {GenderPerAgeChartData, PieChartData} from '../../../../models/chart';
import {TranslateService} from '@ngx-translate/core';
import {BarChartData} from '../../../../models/bar-chart-data';
import {ReferenceDataService} from '../../../services/reference-data/reference-data.service';
import {UserService} from '../../../services/user.service';
import {CommonConstant, GenderEnum} from '../../../app.constants';
import {NumberUtils} from '../../../utils/number.utils';
import {PdfExportService} from '../../../services/export-pdf/pdf-export.service';
import {InfluencerDemographicsComponent, InfluencerDemographicGender} from '../influencer-demographics/influencer-demographics.component';

@Component({
  selector: 'demographic-hype-auditor',
  templateUrl: './demographic-hype-auditor.component.html',
  styleUrls: ['./demographic-hype-auditor.component.scss']
})
export class DemographicHypeAuditorComponent implements OnInit, AfterViewInit {
  static COMPONENT_NAME = 'DemographicHypeAuditorComponent';
  @Input() influencerId: number;
  @Input() pieCenter: number[];
  @Input() pieSize: number;
  @Input() pieInnerSize: number;
  @Input() pieLegend: any;
  @Input() pieHeight: number;
  @Input() pieTitleStyling: any;
  @Input() demographicData: DemographicHypeAuditor;
  @Input() isDeepSocialData: boolean | false;

  public genderPerAgeChart: Array<GenderPerAgeChartData>;
  public genderPerAgeChartMaxPercent: number;

  public languagesBarChartData: Array<BarChartData>;
  public citiesBarChartData: Array<BarChartData>;

  constructor(public translate: TranslateService,
              public referenceDataService: ReferenceDataService,
              public pdfService: PdfExportService,
              public userService: UserService) { }

  ngOnInit() {
    this.buildLanguagesBarChartData();
    this.buildCitiesBarChartData();
    this.genderPerAgeChart = this.transformDemographyAgeChartData(this.demographicData.demographyByAge);
    this.genderPerAgeChartMaxPercent = InfluencerDemographicsComponent.getMaxPercentOfGenderPerAge(this.genderPerAgeChart);
  }

  get genderChartData(): Array<PieChartData> {
    const sortedGenders = [
      ...this.demographicData.genders.filter(x => x.gender === HypeAuditorGenderType.MALE),
      ...this.demographicData.genders.filter(x => x.gender !== HypeAuditorGenderType.MALE)
    ];

    return sortedGenders.map(g => ({
      name: this.getGenderName(g.gender),
      y: g.value / 100,
      color: g.gender === HypeAuditorGenderType.MALE ? this.userService.chartColors[GenderEnum.MALE] : this.userService.chartColors[GenderEnum.FEMALE]
    }));
  }

  get hasGenderChartData() {
    return this.demographicData.genders && this.demographicData.genders.length > 0;
  }

  get countriesChartData(): Array<PieChartData> {
    return this.demographicData.countries.map(c => ({
      name: c.name,
      y: c.value / 100
    }));
  }

  get hasCountriesChartData() {
    return this.demographicData.countries && this.demographicData.countries.length > 0;
  }

  get isGenderPerAgeAvailabel() {
    return this.genderPerAgeChart && this.genderPerAgeChart.length > 0;
  }

  get hasAudienceInterestData() {
    return this.demographicData.audienceInterest
      &&  this.demographicData.audienceInterest.length > 0;
  }

  get firstHalfAudienceInterestChartData(): Array<BarChartData> {
    if (!this.demographicData.audienceInterest) {
      return [];
    }
    const totalAudienceInterest = this.demographicData.audienceInterest.length;
    const firstHalfData = this.demographicData.audienceInterest.slice(0, totalAudienceInterest / 2);
    const result = firstHalfData.map(ai => ({
      title: ai[0].indexOf('and') >= 0 ? ai[0].replace('and', '&') : ai[0].replace('&amp;', '&'),
      value: Math.round(ai[1] * 100),
      formattedValue: Math.round(ai[1] * 100) + '%'
    }));
    return result;
  }

  get secondHalfAudienceInterestChartData(): Array<BarChartData> {
    if (!this.demographicData.audienceInterest) {
      return [];
    }
    const totalAudienceInterest = this.demographicData.audienceInterest.length;
    const secondHalfData = this.demographicData.audienceInterest.slice(totalAudienceInterest / 2, totalAudienceInterest);
    return secondHalfData.map(ai => ({
      title: ai[0].indexOf('and') >= 0 ? ai[0].replace('and', '&') : ai[0].replace('&amp;', '&'),
      value: Math.round(ai[1] * 100),
      formattedValue: Math.round(ai[1] * 100) + '%'
    }));
  }

  get startingIndexOfSecondHalf() {
    if (!this.demographicData.audienceInterest) {
      return 0;
    }
    const totalAudienceInterest = this.demographicData.audienceInterest.length;
    return Math.round((totalAudienceInterest / 2));
  }

  get audienceTypeChartData(): Array<PieChartData> {
    if (!this.demographicData.audienceType ||
      (!this.demographicData.audienceType.real && !this.demographicData.audienceType.infs &&
        !this.demographicData.audienceType.mass && !this.demographicData.audienceType.susp)) {
      return [];
    }
    return this.buildAudienceAndAuthenticChartData(this.demographicData.audienceType.real,
      this.demographicData.audienceType.infs, this.demographicData.audienceType.mass,
      this.demographicData.audienceType.susp);
  }

  get hasAudienceTypeData(): boolean {
    return this.audienceTypeChartData.length > 0;
  }

  private buildLanguagesBarChartData() {
    if (!Array.isArray(this.demographicData.languages)) {
      return;
    }
    this.languagesBarChartData = this.demographicData.languages.map(lang => ({
      title: lang.name ? lang.name : this.getLanguage(lang.code),
      value: lang.value,
      formattedValue: NumberUtils.normalizeNumberToString(lang.value, 1) + '%'
    })).slice(0, 5);
  }

  private buildCitiesBarChartData() {
    if (!Array.isArray(this.demographicData.cities)) {
      return;
    }
    this.citiesBarChartData = this.demographicData.cities.map(lang => ({
      title: lang.name,
      value: lang.value,
      formattedValue: NumberUtils.normalizeNumberToString(lang.value, 1) + '%'
    }));
  }

  get hasCitiesChartData() {
    return this.demographicData.cities && this.demographicData.cities.length > 0;
  }

  get languageChartColor() {
    return this.userService.chartColors[7];
  }

  private getLanguage(lang: string) {
    const language = this.referenceDataService.languages.find(la => la.code === lang);
    return language ? language.name : '';
  }

  private buildAudienceAndAuthenticChartData(real: number, infs: number, mass: number, susp: number): Array<PieChartData> {
    const audienceTypeChartData: Array<PieChartData> = [];
    audienceTypeChartData.push({
      name: this.translate.instant('hype_auditor_demographic.audience_type.real'),
      y: real / 100
    });
    audienceTypeChartData.push({
      name: this.translate.instant('hype_auditor_demographic.audience_type.infs'),
      y: infs / 100
    });
    audienceTypeChartData.push({
      name: this.translate.instant('hype_auditor_demographic.audience_type.mass'),
      y: mass / 100
    });
    audienceTypeChartData.push({
      name: this.translate.instant('hype_auditor_demographic.audience_type.susp'),
      y: susp / 100
    });
    return audienceTypeChartData;
  }

  private getGenderName(gender: HypeAuditorGenderType) {
    if (gender === HypeAuditorGenderType.MALE ) {
      return this.translate.instant('INFLUENCER.CHART.GENDER_PER_AGE.MALE');
    } else {
      return this.translate.instant('INFLUENCER.CHART.GENDER_PER_AGE.FEMALE');
    }
  }

  private transformDemographyAgeChartData(rawData) {
    if (!rawData || rawData.length === 0) {
      return [];
    }

    const maleGroup = rawData[0].by_age_group;
    const femaleGroup = rawData[1].by_age_group;
    let demographyAges: Array<GenderPerAgeChartData> = [];
    const maleAgeGroups = maleGroup.map(g => g.group);
    const femaleAgeGroups = femaleGroup.map(g => g.group);
    const ageGroups = new Set(maleAgeGroups.concat(femaleAgeGroups));
    // sorted set age range
    const sortedAgeGroups = Array.from(ageGroups).sort();
    sortedAgeGroups.forEach(g => {
      const mValue = this.findAgeByGroup(g, maleGroup);
      const fValue = this.findAgeByGroup(g, femaleGroup);
      if (mValue > 0 || fValue > 0) {
        demographyAges.push({
          range: !this.isDeepSocialData ? this.formatAgeGroupName(g) : this.formatDeepSocailAgeGroupName(g),
          f: fValue,
          m: mValue,
        });
      }
    });
    demographyAges = demographyAges.concat(CommonConstant.GENDER_PER_AGE_DEFAULT_VALUE.filter(defaultItem => demographyAges.every(demographyAgesItem => demographyAgesItem.range !== defaultItem.range)))
                      .sort((a, b) => a.range.localeCompare(b.range));
    return demographyAges;
  }

  private formatDeepSocailAgeGroupName(group) {
    if (group === CommonConstant.AGE_RANGE_45_64) {
      return group = CommonConstant.REPLACED_45_64_AGE_RANGE;
    }
    return group;
  }

  private formatAgeGroupName(group) {
    const indexMinorSymbol = group.indexOf('-');
    // if minor in the last, replace it by plus
    if (indexMinorSymbol === (group.length - 1)) {
      return group.substring(3).replace('-', '+');
    } else {
      return group.substring(3);
    }
  }

  private findAgeByGroup(group, data) {
    const matchedGroup = data.find(d => d.group === group);
    return matchedGroup ? Math.round(matchedGroup.value) : 0;
  }

  get isFreeUser() {
    return this.userService.isFreeUser();
  }

  get isAdmin() {
    return this.userService.isAdmin;
  }

  get fallbackTitle() {
    if (this.userService.isFreeUser() && !this.isAdmin) {
      return this.translate.instant('common.invalid_action_by_levels_warning.fallback_title');
    } else {
      return null;
    }
  }

  get fallbackMessage() {
    if (this.userService.isFreeUser() && !this.isAdmin) {
      return this.translate.instant('common.invalid_action_by_levels_warning.fallback_message');
    } else {
      return null;
    }
  }

  get isExportingPdf() {
    return this.pdfService.exportingPdf;
  }

  ngAfterViewInit() {
    this.pdfService.incrementNumberReportReady(DemographicHypeAuditorComponent.COMPONENT_NAME);
  }

  get hasLanguagesChartData() {
    return this.demographicData.languages && this.demographicData.languages.length > 0;
  }

}



// WEBPACK FOOTER //
// ./src/app/main/influencer/demographic-hype-auditor/demographic-hype-auditor.component.ts